import React, { useEffect, useMemo, useState } from "react";
import { PageHeader, Card, KPI, Tag, fmt } from "../components/UI";
import DataTable from "../components/DataTable";
import ReportCharts from "../components/ReportCharts";
import { UserX, X, ListChecks } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth, isFieldRole } from "../context/AuthContext";
import { TimeFilter, periodRange } from "../components/TimeFilter";
import api from "../lib/api";

function Clk({ to, children }) {
  const nav = useNavigate();
  return <div onClick={() => nav(to)} className="cursor-pointer transition-shadow hover:shadow-sm">{children}</div>;
}

const EMPTY_INS = { no_order_this_month: { count: 0, items: [] } };

const monthOf = (s) => (s ? String(s).slice(0, 7) : null);
const dseCharts = [
  { id: "dse-order-value", type: "bar", title: "Order value by DSE", valueLabel: "Order value (₹)", groupBy: (r) => r.name, value: (r) => r.order_value || 0, sort: "value_desc", limit: 10, labelWidth: 130 },
  { id: "dse-active", type: "bar", title: "Active physios by DSE", valueLabel: "Active physios", groupBy: (r) => r.name, value: (r) => r.active_physios || 0, sort: "value_desc", limit: 10, labelWidth: 130 },
  { id: "dse-orders", type: "bar", title: "Orders placed by DSE", valueLabel: "Orders", groupBy: (r) => r.name, value: (r) => r.orders_placed || 0, sort: "value_desc", limit: 10, labelWidth: 130 },
];
const orderCharts = [
  { id: "ord-trend", type: "area", title: "Order value over time", subtitle: "Monthly order value (₹)", valueLabel: "Order value (₹)", groupBy: (r) => monthOf(r.order_date), value: (r) => r.order_value || 0, sort: "time", color: "#0F766E" },
  { id: "ord-physio", type: "bar", title: "Top physios by order value", valueLabel: "Order value (₹)", groupBy: (r) => r.physio_name, value: (r) => r.order_value || 0, sort: "value_desc", limit: 10, labelWidth: 130 },
];

// A KPI tile with an optional "View List" action (Point 28).
function KpiWithList({ label, value, suffix, delta, onView, testId, viewTestId }) {
  return (
    <div className="surface p-4" data-testid={testId}>
      <div className="small-caps text-slate-500">{label}</div>
      <div className="serif-display text-3xl numerals mt-1">{suffix ? <span className="text-lg text-slate-400 mr-0.5">{suffix}</span> : null}{fmt(value)}</div>
      {delta && <div className="text-[11.5px] text-slate-500 mt-0.5">{delta}</div>}
      {onView && (
        <button onClick={onView} className="mt-2 inline-flex items-center gap-1 text-[12px] text-teal-700 hover:underline" data-testid={viewTestId}>
          <ListChecks size={12} /> View List
        </button>
      )}
    </div>
  );
}

export default function SalesDashboard() {
  const { user } = useAuth();
  const field = isFieldRole(user?.role);
  const [d, setD] = useState(null);
  const [ins, setIns] = useState(null);
  const [err, setErr] = useState(null);
  const [modal, setModal] = useState(null); // 'orders' | 'validated' | 'noOrder' | null
  const [preset, setPreset] = useState("this_month");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const nav = useNavigate();

  const range = useMemo(() => periodRange(preset, customStart, customEnd), [preset, customStart, customEnd]);

  useEffect(() => {
    let alive = true;
    (async () => {
      const params = {};
      if (range.start) params.start_date = range.start;
      if (range.end) params.end_date = range.end;
      try {
        const a = await api.get("/analytics/sales", { params });
        if (alive) setD(a.data);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || e.message || "Failed to load dashboard");
        return;
      }
      try {
        const b = await api.get("/dse/insights", { params });
        if (alive) setIns(b.data);
      } catch {
        if (alive) setIns(EMPTY_INS);
      }
    })();
    return () => { alive = false; };
  }, [range.start, range.end]);

  if (err) return <div className="surface p-6 border-l-4 border-rose-500 bg-rose-50/60 text-rose-900 text-sm" data-testid="sales-dashboard-error">Failed to load: {err}</div>;
  if (!d || !ins) return <div className="text-slate-400 text-sm">Loading…</div>;
  const k = d.kpis;

  const timeFilter = (
    <TimeFilter
      preset={preset} setPreset={setPreset}
      customStart={customStart} setCustomStart={setCustomStart}
      customEnd={customEnd} setCustomEnd={setCustomEnd}
      testId="dse-time-filter"
    />
  );

  return (
    <div className="space-y-6">
      <PageHeader
        kicker="Field Operations"
        title="Sales / DSE Dashboard"
        subtitle="Your beat at a glance — physios in scope, orders taken vs validated (by value), and physios yet to order in the selected period."
        testId="sales-dashboard"
      />

      {timeFilter}

      {/* KPI row — Point 28: Order Taken, Order Taken Value, Validated Value with View List */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Clk to="/physios"><KPI label="Physios in scope" value={fmt(k.physios_in_scope)} testId="sales-kpi-scope" /></Clk>
        <KpiWithList label="Order Taken" value={k.orders_taken} delta="unique orders (excl. samples)" onView={() => setModal("orders")} testId="sales-kpi-taken" viewTestId="sales-view-taken" />
        <KpiWithList label="Order Taken Value" suffix="₹" value={k.order_taken_value} delta="PTR × order value" onView={() => setModal("orders")} testId="sales-kpi-taken-value" viewTestId="sales-view-taken-value" />
        <KpiWithList label="Validated Value" suffix="₹" value={k.validated_value} delta={`${k.validation_rate}% validated`} onView={() => setModal("validated")} testId="sales-kpi-validated-value" viewTestId="sales-view-validated" />
      </div>

      {/* No Order Physio (Point 28 list changes) */}
      <button
        onClick={() => setModal("noOrder")}
        data-testid="insight-noorder"
        className="w-full text-left surface p-5 border border-slate-200 bg-slate-50/40 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2"><UserX size={15} className="text-rose-600" /><span className="small-caps text-slate-500">No Order Physio</span></div>
        <div className="serif-display text-4xl numerals mt-2">{fmt(ins.no_order_this_month.count)}</div>
        <div className="text-[12px] text-slate-500 mt-0.5">Physios on your beat with no order in the selected period.</div>
        <div className="text-[12px] text-teal-700 underline mt-2">View list →</div>
      </button>

      {/* Visual insights */}
      {!field && (d.top_dses || []).length > 0 && (
        <ReportCharts rows={d.top_dses} charts={dseCharts} testId="sales" title="DSE performance insights" />
      )}
      {field && (d.orders_detail || []).length > 0 && (
        <ReportCharts rows={d.orders_detail} charts={orderCharts} testId="sales-field" title="Order insights" />
      )}

      {/* Top DSEs leaderboard (Point 24 fix — mapped by ERP) — hidden for field users */}
      {!field && (
        <Card title="Top DSEs — performance" subtitle="Active/Inactive physios, orders placed and order value per DSE">
          <DataTable
            columns={[
              { key: "name", label: "DSE", filter: "text" },
              { key: "territory", label: "Territory", filter: "text", render: (r) => r.territory || "—" },
              { key: "active_physios", label: "Active physios", filter: "range", align: "right", render: (r) => fmt(r.active_physios) },
              { key: "inactive_physios", label: "Inactive physios", filter: "range", align: "right", render: (r) => fmt(r.inactive_physios) },
              { key: "orders_placed", label: "Orders placed", filter: "range", align: "right", render: (r) => fmt(r.orders_placed) },
              { key: "order_value", label: "Order value (₹)", filter: "range", align: "right", render: (r) => fmt(r.order_value) },
              { key: "points_driven", label: "Points driven", filter: "range", align: "right", render: (r) => fmt(r.points_driven) },
            ]}
            data={d.top_dses} rowKey="id"
            onRowClick={() => nav(`/hierarchy`)}
            searchableKeys={["name", "territory"]}
            exportFilename="cbc-sales-dses"
            testIdPrefix="sales-dses"
            initialSort={{ key: "order_value", dir: "desc" }}
            paginated pageSize={25}
          />
        </Card>
      )}

      {modal && (
        <SalesModal
          modal={modal}
          orders={d.orders_detail || []}
          noOrder={ins.no_order_this_month.items || []}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString(); } catch { return String(s).slice(0, 10); }
}

function SalesModal({ modal, orders, noOrder, onClose }) {
  let title = "", subtitle = "", columns = [], data = [], filename = "", testIdPrefix = "", rowKey = "order_ref";

  if (modal === "orders" || modal === "validated") {
    const isValidated = modal === "validated";
    title = isValidated ? "Validated (dispatched) orders" : "Orders taken";
    subtitle = isValidated
      ? "Unique orders with a partial or full dispatch. Validated value = PTR × dispatch value."
      : "Unique orders placed in the selected period (samples excluded). Value = PTR × order value.";
    columns = [
      { key: "order_ref", label: "Order ref", filter: "text", render: (r) => <span className="font-mono text-[11px]">{r.order_ref || "—"}</span> },
      { key: "physio_name", label: "Physio", filter: "text" },
      { key: "mobile", label: "Mobile", filter: "text", render: (r) => r.mobile || "—" },
      { key: "erp_outlet_id", label: "ERP outlet", filter: "text", render: (r) => r.erp_outlet_id || "—" },
      { key: "beat", label: "Beat", filter: "text", render: (r) => r.beat || "—" },
      { key: "order_value", label: "Order value (₹)", filter: "range", align: "right", render: (r) => fmt(r.order_value) },
      { key: "dispatch_value", label: "Dispatch value (₹)", filter: "range", align: "right", render: (r) => fmt(r.dispatch_value) },
      { key: "order_date", label: "Order date", render: (r) => fmtDate(r.order_date), accessor: (r) => r.order_date },
      { key: "dispatch_status", label: "Dispatch", filter: "text", render: (r) => r.dispatch_status || "—" },
    ];
    data = isValidated ? orders.filter((o) => (o.dispatch_value || 0) > 0) : orders;
    filename = isValidated ? "cbc-validated-orders" : "cbc-orders-taken";
    testIdPrefix = "modal-orders";
  } else if (modal === "noOrder") {
    title = "No Order Physio";
    subtitle = "Physios on your beat with no order in the selected period. Plan a visit or WhatsApp nudge.";
    columns = [
      { key: "physio_name", label: "Physio", filter: "text" },
      { key: "physio_code", label: "ERP ID", filter: "text", render: (r) => <span className="font-mono text-[11px]">{r.physio_code || r.outlet_erp || "—"}</span> },
      { key: "beat", label: "Beat", filter: "text", render: (r) => r.beat || "—" },
      { key: "outlet_erp", label: "Outlet ERP", filter: "text", render: (r) => <span className="font-mono text-[11px]">{r.outlet_erp || "—"}</span> },
      { key: "last_order_date", label: "Last Order Date", render: (r) => fmtDate(r.last_order_date), accessor: (r) => r.last_order_date },
    ];
    data = noOrder;
    filename = "cbc-no-order-physio";
    testIdPrefix = "modal-noorder";
    rowKey = "physio_id";
  }

  return (
    <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl border border-slate-200 w-full max-w-6xl max-h-[88vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid={`${testIdPrefix}-modal`}>
        <div className="p-5 border-b border-slate-100 flex items-start justify-between">
          <div>
            <h3 className="serif-display text-2xl">{title}</h3>
            <p className="text-[12.5px] text-slate-500 mt-1">{subtitle}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid={`${testIdPrefix}-close`}><X size={18} /></button>
        </div>
        <div className="p-5 overflow-auto">
          {data.length === 0 ? (
            <div className="text-slate-400 text-sm text-center py-12">No records — all clear.</div>
          ) : (
            <DataTable
              columns={columns}
              data={data}
              rowKey={rowKey}
              searchableKeys={["physio_name", "physio_code", "order_ref", "beat"]}
              exportFilename={filename}
              testIdPrefix={testIdPrefix}
              paginated pageSize={25}
            />
          )}
        </div>
      </div>
    </div>
  );
}
